import {
  Box,
  type BoxProps,
  Skeleton,
  type SkeletonProps,
  Typography,
  type TypographyProps,
} from "@mui/material"
import { type PropsWithChildren, Suspense, type SuspenseProps } from "react"
import { ErrorBoundary, type ErrorBoundaryProps } from "react-error-boundary"
import type { MergeExclusive } from "type-fest"

export type SafeApiComponentProps = PropsWithChildren<{
  errorBoundary?: Omit<
    ErrorBoundaryProps,
    "children" | "FallbackComponent" | "fallback"
  > & {
    boxProps?: BoxProps
    typoProps?: TypographyProps
  }
  hideError?: boolean
  errorMsg?: string
  suspense?: MergeExclusive<
    { fallback: SuspenseProps["fallback"] },
    { skeletonProps: SkeletonProps }
  >
  hideSkeleton?: boolean
}>

const defaultErrorMsg = "⚠️ Une erreur est survenue"

/**
 * The error block `SafeApiComponent` falls back to.
 * Exported so a component that detects an invalid state *before* any throw
 * (an unparseable URL param, for instance) can render the very same state.
 */
export const ApiErrorMessage = ({
  errorMsg = defaultErrorMsg,
  boxProps,
  typoProps,
}: {
  errorMsg?: string
  boxProps?: BoxProps
  typoProps?: TypographyProps
}) => (
  <Box
    display="flex"
    width="100%"
    height="100%"
    alignItems="center"
    justifyContent="center"
    {...boxProps}
  >
    <Typography {...typoProps}>{errorMsg}</Typography>
  </Box>
)

export const SafeApiComponent = ({
  errorBoundary,
  hideError = false,
  hideSkeleton = false,
  errorMsg = defaultErrorMsg,
  suspense,
  children,
}: SafeApiComponentProps) => {
  const skeletonProps = suspense?.skeletonProps
  return (
    <ErrorBoundary
      {...errorBoundary}
      fallbackRender={
        hideError
          ? () => null
          : (errorBoundary?.fallbackRender ??
            (() => (
              <ApiErrorMessage
                errorMsg={errorMsg}
                boxProps={errorBoundary?.boxProps}
                typoProps={errorBoundary?.typoProps}
              />
            )))
      }
    >
      <Suspense
        fallback={
          hideSkeleton
            ? null
            : (suspense?.fallback ?? (
                <Skeleton
                  {...skeletonProps}
                  variant={skeletonProps?.variant ?? "rounded"}
                  sx={{
                    width: skeletonProps?.width ?? "100%",
                    height: skeletonProps?.height ?? "100%",
                    ...skeletonProps?.sx,
                  }}
                >
                  {skeletonProps?.children}
                </Skeleton>
              ))
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}
